import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth, hasRole } from "../lib/auth";
import { PageHeader, Section } from "../components/Primitives";
import Pagination, { usePagination } from "../components/Pagination";
import SyncProgressModal from "../components/SyncProgressModal";
import RegionPicker from "../components/RegionPicker";
import {
    Plus,
    RefreshCcw,
    History,
    X,
    KeyRound,
    ShieldCheck,
    AlertTriangle,
    Settings2,
    Trash2,
    Eye,
    EyeOff,
    ExternalLink,
    Link2,
    Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useApiLoading, LoadingRow } from "../components/TableLoader";
import { parseUtc } from "../lib/datetime";

// Renders a single `credentials_masked` value. If the backend returns a nested object
// (e.g. DV360 service-account keys → `{type, project_id, client_email, private_key, ...}`),
// stringify it for display instead of passing the raw object as a React child (React error #31).
const formatMaskedValue = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "string" || typeof v === "number") return v;
    if (typeof v === "object") {
        // Show the two most human-recognisable keys first when present.
        const preferred = v.client_email || v.project_id;
        if (preferred) return preferred;
        return JSON.stringify(v);
    }
    return String(v);
};


// Platform credential schemas
const PLATFORM_FIELDS = {
    meta: [
        { key: "app_id", label: "App ID", secret: false, hint: "Meta for Developers → My Apps → your app → App Settings → Basic." },
        { key: "app_secret", label: "App Secret", secret: true, hint: "From the same page. Never expose in client code." },
        { key: "access_token", label: "System User Access Token", secret: true, hint: "Business Manager → Users → System Users → Generate Token (Marketing API scope)." },
        { key: "business_id", label: "Business ID", secret: false, hint: "Business Manager → Business Info." },
    ],
    dv360: [
        { key: "client_id", label: "OAuth Client ID", secret: false, hint: "Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client ID (Web application)." },
        { key: "client_secret", label: "OAuth Client Secret", secret: true, hint: "Same OAuth credential page. Required to exchange the auth code for tokens." },
    ],
    tiktok: [
        { key: "app_id", label: "App ID", secret: false, hint: "TikTok for Business Developer Portal → your Marketing API app → Basic info." },
        { key: "secret", label: "App Secret", secret: true, hint: "Same page. Required to exchange the auth code for an access_token during OAuth." },
    ],
};

const PLATFORM_DOCS = {
    meta: "https://developers.facebook.com/docs/marketing-api/get-started",
    dv360: "https://developers.google.com/display-video/api/guides/getting-started/overview",
    tiktok: "https://business-api.tiktok.com/portal/docs?id=1738455508553218",
};

const STATUS_CLASS = {
    verified: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    failed: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function AdAccounts() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const apiLoading = useApiLoading();
    const [connections, setConnections] = useState([]);
    const [advertisers, setAdvertisers] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [history, setHistory] = useState(null);

    // Connect / edit credentials modal state
    const [credModal, setCredModal] = useState(null);
    // shape: { mode: "create" | "edit", platform, display_name, external_id, agency_id, scope_notes, creds, show: {field:bool}, conn_id? }

    const [syncJobId, setSyncJobId] = useState(null);
    const [redirectUris, setRedirectUris] = useState({ meta: "", google: "" });

    const accPager = usePagination(list, 25);

    const load = async () => {
        const [a, c, adv] = await Promise.all([
            api.get("/ad-accounts"),
            hasRole(user, "super_admin", "admin") ? api.get("/platform-connections") : Promise.resolve({ data: [] }),
            hasRole(user, "super_admin", "admin") ? api.get("/advertisers") : Promise.resolve({ data: [] }),
        ]);
        setList(a.data);
        setConnections(c.data);
        setAdvertisers(adv.data);
    };

    useEffect(() => {
        load();
        if (user.role === "super_admin") api.get("/agencies").then((r) => setAgencies(r.data));
        // Fetch OAuth redirect URIs once for help text
        api.get("/integrations/oauth/redirect-uri").then((r) => setRedirectUris(r.data)).catch(() => {});
    }, [user.role]); // eslint-disable-line

    // Listen for OAuth popup result
    useEffect(() => {
        const handler = (ev) => {
            if (ev?.data?.type === "oauth_result") {
                if (ev.data.status === "ok") {
                    toast.success("OAuth connected — credentials saved & verified.");
                    setCredModal(null);
                    load();
                } else {
                    toast.error("OAuth failed — check the popup window for details.");
                    load();
                }
            }
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, []); // eslint-disable-line

    const sync = async (id) => {
        try {
            const { data } = await api.post(`/platform-connections/${id}/sync?days=30`);
            if (data.reused) {
                toast.info("A sync is already in progress");
            } else {
                toast.success("Sync queued — opening progress");
            }
            setSyncJobId(data.job_id);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sync failed");
        }
    };

    const test = async (id) => {
        try {
            const { data } = await api.post(`/platform-connections/${id}/test`);
            if (data.connection_status === "verified") toast.success("Connection verified");
            else toast.error(data.last_test_error || "Verification failed");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test failed");
        }
    };

    const removeConn = async (id) => {
        if (!window.confirm("Delete this connection? All ad accounts synced from it will be DEACTIVATED (you can reactivate them later by reconnecting). Historical insights and assignments are preserved.")) return;
        try {
            const { data } = await api.delete(`/platform-connections/${id}`);
            const n = data?.deactivated_ad_accounts || 0;
            toast.success(n > 0 ? `Connection removed · ${n} ad account${n === 1 ? "" : "s"} deactivated` : "Connection removed");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const assign = async (accId, advId) => {
        try {
            if (advId) {
                await api.post(`/ad-accounts/${accId}/assign`, { advertiser_id: advId });
                toast.success("Assignment updated");
            } else {
                if (!window.confirm("Unassign this advertiser? The ad account will no longer appear in their portal.")) return;
                await api.post(`/ad-accounts/${accId}/unassign`);
                toast.success("Advertiser unassigned");
            }
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const updateRegion = async (accId, region) => {
        // Optimistic UI — patch the row immediately, revert if the API fails.
        const prev = list.find((a) => a.id === accId)?.region || "";
        setList((rows) => rows.map((a) => a.id === accId ? { ...a, region } : a));
        try {
            await api.patch(`/ad-accounts/${accId}`, { region });
            toast.success(region ? `Region set to ${region}` : "Region cleared");
        } catch (e) {
            setList((rows) => rows.map((a) => a.id === accId ? { ...a, region: prev } : a));
            toast.error(e.response?.data?.detail || "Failed to update region");
        }
    };

    const updateConnectionRegion = async (connId, region) => {
        // Optimistic UI on the connection AND all its child ad accounts (cascade preview).
        const prevConn = connections.find((c) => c.id === connId)?.region || "";
        setConnections((rows) => rows.map((c) => c.id === connId ? { ...c, region } : c));
        setList((rows) => rows.map((a) => a.platform_connection_id === connId ? { ...a, region } : a));
        try {
            await api.patch(`/platform-connections/${connId}`, { region });
            toast.success(region
                ? `Region set to ${region} — cascaded to all ad accounts under this connection`
                : "Region cleared on this connection");
        } catch (e) {
            // Revert both connection and ad accounts on failure. Simpler: re-fetch.
            setConnections((rows) => rows.map((c) => c.id === connId ? { ...c, region: prevConn } : c));
            load();
            toast.error(e.response?.data?.detail || "Failed to update region");
        }
    };

    const showHistory = async (accId) => {
        const { data } = await api.get(`/ad-accounts/${accId}/assignments`);
        setHistory({ accId, rows: data });
    };

    const openCreate = () => {
        setCredModal({
            mode: "create",
            platform: "meta",
            display_name: "",
            external_id: "",
            agency_id: user.role === "admin" ? user.agency_id : "",
            scope_notes: "",
            creds: {},
            show: {},
            // DV360 default to Service Account (recommended for unattended sync)
            dv360_auth_type: "service_account",
            sa_json_text: "",
            sa_json_parsed: null,
            sa_email: "",
            sa_error: "",
            // TikTok defaults — paste-token is the smoother onboarding
            tiktok_auth_type: "paste_token",
            tiktok_access_token: "",
        });
    };
    const openEditCreds = (conn) => {
        setCredModal({
            mode: "edit",
            conn_id: conn.id,
            platform: conn.platform,
            display_name: conn.display_name,
            external_id: conn.external_id,
            agency_id: conn.agency_id,
            scope_notes: conn.scope_notes || "",
            creds: {}, // start blank — backend merges over existing
            existing_masked: conn.credentials_masked || {},
            dv360_auth_type: conn.platform === "dv360" ? (conn.auth_type === "service_account" ? "service_account" : "oauth") : "service_account",
            show: {},
        });
    };

    const submitCreds = async (e) => {
        e.preventDefault();
        const m = credModal;
        try {
            // DV360 → Service Account path: create or update an existing connection.
            if (m.platform === "dv360" && m.dv360_auth_type === "service_account") {
                if (!m.sa_json_parsed) {
                    toast.error("Upload or paste a valid Google service-account JSON key first.");
                    return;
                }
                if (m.mode === "create") {
                    const payload = {
                        display_name: m.display_name,
                        external_id: m.external_id,
                        service_account_json: m.sa_json_parsed,
                        scope_notes: m.scope_notes,
                    };
                    if (user.role === "super_admin") payload.agency_id = m.agency_id;
                    const { data } = await api.post("/dv360/connect/service-account", payload);
                    toast.success(`DV360 verified as ${data.service_account_email}. Ready to sync.`);
                } else {
                    const { data } = await api.put(`/dv360/connections/${m.conn_id}/service-account`, {
                        display_name: m.display_name,
                        external_id: m.external_id,
                        service_account_json: m.sa_json_parsed,
                        scope_notes: m.scope_notes,
                    });
                    toast.success(`DV360 re-verified as ${data.service_account_email}.`);
                }
                setCredModal(null);
                load();
                return;
            }

            // TikTok → Paste Token path: single call to /tiktok/connect/paste-token
            if (m.mode === "create" && m.platform === "tiktok" && m.tiktok_auth_type === "paste_token") {
                if (!m.tiktok_access_token || !m.creds.app_id || !m.external_id) {
                    toast.error("App ID, Advertiser ID, and Long-lived Access Token are all required.");
                    return;
                }
                const payload = {
                    display_name: m.display_name,
                    app_id: m.creds.app_id,
                    advertiser_id: m.external_id,
                    access_token: m.tiktok_access_token,
                    scope_notes: m.scope_notes,
                };
                if (user.role === "super_admin") payload.agency_id = m.agency_id;
                await api.post("/tiktok/connect/paste-token", payload);
                toast.success("TikTok connection verified. Ready to sync.");
                setCredModal(null);
                load();
                return;
            }

            let connectionId = m.conn_id;
            if (m.mode === "create") {
                const payload = {
                    platform: m.platform,
                    display_name: m.display_name,
                    external_id: m.external_id,
                    scope_notes: m.scope_notes,
                    credentials: m.creds,
                };
                if (user.role === "super_admin") payload.agency_id = m.agency_id;
                const { data } = await api.post("/platform-connections", payload);
                connectionId = data.id;
                if (m.platform === "meta") {
                    // Meta tests immediately; DV360/TikTok require OAuth exchange first.
                    await api.post(`/platform-connections/${data.id}/test`);
                }
                toast.success("Connection added — verifying credentials…");
            } else {
                // DV360 switching from Service Account → User OAuth needs real OAuth creds
                if (m.platform === "dv360" && m.dv360_auth_type === "oauth" && !m.existing_masked?.client_id) {
                    if (!m.creds.client_id || !m.creds.client_secret) {
                        toast.error("Enter the OAuth Client ID and Client Secret to switch this connection to User OAuth.");
                        return;
                    }
                }
                await api.put(`/platform-connections/${connectionId}/credentials`, m.creds);
                if (m.platform === "meta") {
                    await api.post(`/platform-connections/${connectionId}/test`);
                }
                toast.success("Credentials updated");
            }

            // DV360 or TikTok OAuth mode → auto-open the OAuth popup right after saving creds.
            if (m.platform === "dv360" || m.platform === "tiktok") {
                try {
                    const path = m.platform === "dv360" ? "dv360" : "tiktok";
                    const { data: oauth } = await api.get(`/oauth/${path}/auth-url?connection_id=${connectionId}`);
                    const popup = window.open(oauth.authorize_url, "oauth_popup", "width=560,height=720,scrollbars=yes");
                    if (!popup) {
                        toast.error("Popup blocked — allow popups and click 'Connect via OAuth' from the connection card.");
                    } else {
                        toast.info("Opening authorisation window — approve access, then this dialog will close automatically.");
                    }
                } catch (err) {
                    toast.error(err.response?.data?.detail || `Failed to start ${m.platform} OAuth`);
                }
            }
            setCredModal(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    // Parse an uploaded/pasted service-account JSON string. Returns {ok, parsed?, email?, error?}.
    const parseSAJson = (text) => {
        try {
            const j = JSON.parse(text);
            if (j?.type !== "service_account") return { ok: false, error: "Not a service-account key (missing type='service_account')" };
            if (!j?.client_email || !j?.private_key) return { ok: false, error: "Missing client_email or private_key" };
            return { ok: true, parsed: j, email: j.client_email };
        } catch (err) {
            return { ok: false, error: "Invalid JSON: " + (err.message || "parse error") };
        }
    };

    const onSAFileUpload = async (file) => {
        if (!file) return;
        const text = await file.text();
        const r = parseSAJson(text);
        setCredModal((prev) => ({
            ...prev,
            sa_json_text: text,
            sa_json_parsed: r.ok ? r.parsed : null,
            sa_email: r.ok ? r.email : "",
            sa_error: r.ok ? "" : r.error,
        }));
    };

    const onSATextChange = (text) => {
        const r = text.trim() ? parseSAJson(text) : { ok: false };
        setCredModal((prev) => ({
            ...prev,
            sa_json_text: text,
            sa_json_parsed: r.ok ? r.parsed : null,
            sa_email: r.ok ? r.email : "",
            sa_error: r.ok || !text.trim() ? "" : r.error,
        }));
    };

    const fields = credModal ? PLATFORM_FIELDS[credModal.platform] || [] : [];

    return (
        <div data-testid="ad-accounts-page">
            <PageHeader
                eyebrow="Integrations"
                title="Ad Accounts"
                subtitle="Connect Meta or DV360 with real API credentials, verify, then sync ad accounts and assign them to advertisers — assignment history is always preserved."
                actions={
                    hasRole(user, "super_admin", "admin") && user.can_connect_integrations !== false && (
                        <button onClick={openCreate} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="connect-platform-btn">
                            <Plus className="h-4 w-4" /> Connect platform
                        </button>
                    )
                }
            />

            {hasRole(user, "super_admin", "admin") && (
                <Section title="Platform connections" testId="section-connections">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {connections.map((c) => (
                            <div key={c.id} className="bg-white border border-zinc-200 p-4 hover:border-zinc-300 transition-colors">
                                <div className="flex items-start justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="overline flex items-center gap-2">
                                            {c.platform}
                                            {(c.platform === "dv360" || c.platform === "tiktok") && c.auth_type && (
                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium tracking-wider ${c.auth_type === "service_account" ? "bg-sky-500/10 text-sky-500 border border-sky-500/20" : c.auth_type === "paste_token" ? "bg-purple-500/10 text-purple-500 border border-purple-500/20" : "bg-zinc-100 text-zinc-600 border border-zinc-200"}`}>
                                                    {c.auth_type === "service_account" ? "SA" : c.auth_type === "paste_token" ? "TOKEN" : "OAUTH"}
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-display text-base text-zinc-900 mt-1 truncate" title={c.display_name}>{c.display_name}</div>
                                        <div className="text-xs text-zinc-500 mt-0.5 font-mono truncate">{c.external_id}</div>
                                        {c.service_account_email && (
                                            <div className="text-[10px] text-zinc-500 mt-1 truncate" title={c.service_account_email}>
                                                <span className="text-zinc-400">SA:</span> <span className="font-mono">{c.service_account_email}</span>
                                            </div>
                                        )}
                                    </div>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${STATUS_CLASS[c.connection_status] || STATUS_CLASS.pending}`} data-testid={`conn-status-${c.id}`}>
                                        {c.connection_status === "verified" && <ShieldCheck className="h-3 w-3" />}
                                        {c.connection_status === "failed" && <AlertTriangle className="h-3 w-3" />}
                                        {c.connection_status === "pending" && <KeyRound className="h-3 w-3" />}
                                        {c.connection_status}
                                    </span>
                                </div>
                                {c.last_test_error && (
                                    <div className="mt-2 text-[11px] text-red-400 bg-red-500/5 border border-red-500/20 px-2 py-1 rounded">{c.last_test_error}</div>
                                )}
                                {c.token_expires_at && (() => {
                                    const exp = new Date(c.token_expires_at);
                                    const days = Math.round((exp - Date.now()) / (1000 * 60 * 60 * 24));
                                    if (days <= 0) {
                                        return <div className="mt-2 text-[11px] text-red-400 bg-red-500/5 border border-red-500/20 px-2 py-1 rounded inline-flex items-center gap-1"><Clock className="h-3 w-3" />Token expired — re-authorize now</div>;
                                    }
                                    if (days <= 7) {
                                        return <div className="mt-2 text-[11px] text-amber-400 bg-amber-500/5 border border-amber-500/20 px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`token-expiry-warning-${c.id}`}><Clock className="h-3 w-3" />Token expires in {days} day{days === 1 ? "" : "s"} ({exp.toLocaleDateString()})</div>;
                                    }
                                    return <div className="mt-2 text-[11px] text-zinc-500 inline-flex items-center gap-1"><Clock className="h-3 w-3" />Token valid for {days} days</div>;
                                })()}
                                {c.credentials_masked && Object.keys(c.credentials_masked).length > 0 && (
                                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                                        {Object.entries(c.credentials_masked).slice(0, 4).map(([k, v]) => (
                                            <div key={k} className="truncate">
                                                <span className="text-zinc-500">{k.replace(/_/g, " ")}:</span>{" "}
                                                <span className="text-zinc-700 font-mono">{formatMaskedValue(v)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Last sync + next auto-sync */}
                                {c.connection_status === "verified" && (() => {
                                    const interval = (c.sync_interval_minutes || 240) * 60 * 1000;
                                    const last = parseUtc(c.last_synced_at);
                                    if (!last) {
                                        return (
                                            <div className="mt-2 text-[11px] inline-flex items-center gap-1.5 text-amber-500" data-testid={`last-sync-${c.id}`}>
                                                <RefreshCcw className="h-3 w-3" />Never synced — auto-sync runs within 15 min
                                            </div>
                                        );
                                    }
                                    const next = new Date(last.getTime() + interval);
                                    const minsAgo = Math.round((Date.now() - last.getTime()) / 60000);
                                    const ago =
                                        minsAgo < 1 ? "just now" :
                                        minsAgo < 60 ? `${minsAgo} min ago` :
                                        minsAgo < 1440 ? `${Math.round(minsAgo / 60)} h ago` :
                                        `${Math.round(minsAgo / 1440)} d ago`;
                                    const nextMins = Math.max(0, Math.round((next.getTime() - Date.now()) / 60000));
                                    const nextLabel =
                                        nextMins < 1 ? "in <1 min" :
                                        nextMins < 60 ? `in ${nextMins} min` :
                                        `in ${Math.round(nextMins / 60)} h`;
                                    return (
                                        <div className="mt-2 text-[11px] flex items-center gap-3 flex-wrap" data-testid={`last-sync-${c.id}`}>
                                            <span className="inline-flex items-center gap-1.5 text-zinc-700">
                                                <RefreshCcw className="h-3 w-3 wl-accent-text" />
                                                Last sync: <span className="font-medium">{ago}</span>
                                                <span className="text-zinc-400">({last.toLocaleString()})</span>
                                            </span>
                                            <span className="text-zinc-400">·</span>
                                            <span className="text-zinc-500">Next auto-sync {nextLabel}</span>
                                        </div>
                                    );
                                })()}
                                {/* Sync interval selector */}
                                {c.connection_status === "verified" && (
                                    <div className="mt-2 text-[11px] flex items-center gap-2 text-zinc-500" data-testid={`sync-interval-row-${c.id}`}>
                                        <span>Auto-sync every</span>
                                        <select
                                            value={c.sync_interval_minutes || 240}
                                            onChange={async (e) => {
                                                const minutes = parseInt(e.target.value, 10);
                                                try {
                                                    await api.put(`/platform-connections/${c.id}/sync-interval`, { sync_interval_minutes: minutes });
                                                    toast.success(`Sync interval updated to ${minutes < 60 ? minutes + " min" : (minutes / 60) + " h"}`);
                                                    load();
                                                } catch (err) {
                                                    toast.error(err.response?.data?.detail || "Failed");
                                                }
                                            }}
                                            className="px-1.5 py-0.5 bg-white border border-zinc-300 rounded text-[11px] tabular-nums"
                                            data-testid={`sync-interval-${c.id}`}
                                        >
                                            <option value={30}>30 min</option>
                                            <option value={60}>1 hour</option>
                                            <option value={120}>2 hours</option>
                                            <option value={240}>4 hours</option>
                                            <option value={360}>6 hours</option>
                                            <option value={720}>12 hours</option>
                                            <option value={1440}>24 hours</option>
                                        </select>
                                    </div>
                                )}
                                {/* Region — default country for every ad account synced under this connection. */}
                                <div className="mt-2" data-testid={`conn-region-row-${c.id}`}>
                                    <div className="text-[11px] text-zinc-500 mb-1">Default region for all ad accounts under this connection</div>
                                    <RegionPicker
                                        value={c.region || ""}
                                        onChange={(v) => updateConnectionRegion(c.id, v)}
                                        placeholder="Set country (e.g. Malaysia)…"
                                        testId={`conn-region-picker-${c.id}`}
                                    />
                                </div>
                                <div className="mt-3 flex items-center gap-3 text-xs">
                                    <button onClick={() => test(c.id)} className="inline-flex items-center gap-1 text-zinc-700 hover:wl-accent-text" data-testid={`test-${c.id}`}>
                                        <ShieldCheck className="h-3 w-3" />test
                                    </button>
                                    <button onClick={() => sync(c.id)} disabled={c.connection_status !== "verified"} className="inline-flex items-center gap-1 text-zinc-700 hover:wl-accent-text disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`sync-${c.id}`} title={c.connection_status !== "verified" ? "Verify the connection first" : "Pull ad accounts and campaigns"}>
                                        <RefreshCcw className="h-3 w-3" />sync
                                    </button>
                                    <button onClick={() => openEditCreds(c)} className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900" data-testid={`edit-creds-${c.id}`}>
                                        <Settings2 className="h-3 w-3" />creds
                                    </button>
                                    <button onClick={() => removeConn(c.id)} className="ml-auto text-red-400 hover:text-red-300" data-testid={`delete-conn-${c.id}`}>
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {!connections.length && (
                            <div className="col-span-full bg-zinc-50 border border-dashed border-zinc-200 p-6 text-center text-zinc-500 text-sm">
                                No platform connections yet. Click &quot;Connect platform&quot; to add Meta or Google with real API credentials.
                            </div>
                        )}
                    </div>
                </Section>
            )}

            <Section title="Ad accounts" testId="section-accounts">
                <div className="bg-white border border-zinc-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-zinc-100 border-b border-zinc-200">
                                <th className="overline text-left p-3">Account</th>
                                <th className="overline text-left p-3">Platform</th>
                                <th className="overline text-left p-3">Region</th>
                                <th className="overline text-left p-3">Assigned to</th>
                                <th className="overline text-left p-3">Currency</th>
                                <th className="overline text-right p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {accPager.slice.map((a) => (
                                <tr key={a.id} className="border-b border-zinc-200 last:border-0">
                                    <td className="p-3">
                                        <div className="text-zinc-900">{a.name}</div>
                                        <div className="text-xs text-zinc-500 font-mono">{a.external_account_id}</div>
                                    </td>
                                    <td className="p-3 text-zinc-700 capitalize">{a.platform}</td>
                                    <td className="p-3" style={{ minWidth: 180 }}>
                                        {hasRole(user, "super_admin", "admin") ? (
                                            <div data-testid={`region-${a.id}`}>
                                                <RegionPicker value={a.region || ""} onChange={(v) => updateRegion(a.id, v)} placeholder="Set country…" testId={`region-picker-${a.id}`} />
                                            </div>
                                        ) : (
                                            <span className="text-zinc-700">{a.region || "—"}</span>
                                        )}
                                    </td>
                                    <td className="p-3">
                                        {hasRole(user, "super_admin", "admin") ? (
                                            <select value={a.current_advertiser_id || ""} onChange={(e) => assign(a.id, e.target.value)} className="px-2 py-1 bg-zinc-100 border border-zinc-200 rounded text-xs" data-testid={`assign-${a.id}`}>
                                                <option value="">— Unassigned —</option>
                                                {advertisers.map((adv) => (<option key={adv.id} value={adv.id}>{adv.name}</option>))}
                                            </select>
                                        ) : (
                                            <span className="text-zinc-700">{a.current_advertiser_name || "—"}</span>
                                        )}
                                    </td>
                                    <td className="p-3 text-zinc-500">{a.currency}</td>
                                    <td className="p-3 text-right">
                                        <button onClick={() => showHistory(a.id)} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:wl-accent-text" data-testid={`history-${a.id}`}>
                                            <History className="h-3 w-3" />history
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!list.length && (apiLoading ? <LoadingRow colSpan={6} /> : <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No ad accounts yet. Connect a platform and click Sync.</td></tr>)}
                        </tbody>
                    </table>
                    {list.length > 0 && (
                        <Pagination
                            page={accPager.page}
                            totalPages={accPager.totalPages}
                            pageSize={accPager.pageSize}
                            range={accPager.range}
                            setPage={accPager.setPage}
                            setPageSize={accPager.setPageSize}
                            dataTestId="ad-accounts-pagination"
                        />
                    )}
                </div>
            </Section>

            {credModal && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setCredModal(null)}>
                    <form onSubmit={submitCreds} onClick={(e) => e.stopPropagation()} className="bg-zinc-50 border border-zinc-200 w-full max-w-2xl p-6 rounded-md my-8 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="connect-modal">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="overline">{credModal.mode === "create" ? "Connect platform" : "Edit credentials"}</div>
                                <h3 className="font-display text-xl mt-1">
                                    {credModal.platform === "meta" ? "Meta Marketing API" : credModal.platform === "tiktok" ? "TikTok Marketing API" : "Display & Video 360"}
                                </h3>
                            </div>
                            <button type="button" onClick={() => setCredModal(null)}><X className="h-4 w-4 text-zinc-500" /></button>
                        </div>

                        {credModal.mode === "create" && (
                            <>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="overline">Platform</label>
                                    <select value={credModal.platform} onChange={(e) => setCredModal({ ...credModal, platform: e.target.value, creds: {} })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="connect-platform-select">
                                        <option value="meta">Meta Ads</option>
                                        <option value="dv360">DV360 (Display & Video 360)</option>
                                        <option value="tiktok">TikTok Ads (Marketing API)</option>
                                    </select>
                                </div>
                                {user.role === "super_admin" && (
                                    <div>
                                        <label className="overline">Agency</label>
                                        <select required value={credModal.agency_id} onChange={(e) => setCredModal({ ...credModal, agency_id: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded">
                                            <option value="">-- pick agency --</option>
                                            {agencies.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                        </select>
                                    </div>
                                )}
                                <div className={credModal.platform === "dv360" || credModal.platform === "tiktok" ? "col-span-2" : ""}>
                                    <label className="overline">Display name</label>
                                    <input required value={credModal.display_name} onChange={(e) => setCredModal({ ...credModal, display_name: e.target.value })} placeholder={credModal.platform === "dv360" ? "e.g. ACME DV360" : credModal.platform === "tiktok" ? "e.g. ACME TikTok" : "e.g. NorthStar Meta Business"} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="connect-name-input" />
                                </div>
                            </div>

                            {/* DV360 → auth-type tabs (create mode only) */}
                            {credModal.platform === "dv360" && credModal.mode === "create" && (
                                <div className="mb-4">
                                    <label className="overline">Auth method</label>
                                    <div className="mt-2 grid grid-cols-2 gap-2" data-testid="dv360-auth-tabs">
                                        {[
                                            { k: "service_account", title: "Service Account", subtitle: "Recommended · no consent screen" },
                                            { k: "oauth", title: "User OAuth", subtitle: "Sign in with your Google account" },
                                        ].map((t) => (
                                            <button
                                                type="button"
                                                key={t.k}
                                                onClick={() => setCredModal({ ...credModal, dv360_auth_type: t.k })}
                                                data-testid={`dv360-auth-${t.k}`}
                                                className={`text-left px-3 py-2 rounded border transition-colors ${credModal.dv360_auth_type === t.k ? "border-sky-500 bg-sky-500/5" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                                            >
                                                <div className="text-sm font-medium text-zinc-900 flex items-center gap-1.5">
                                                    {t.k === "service_account" ? <ShieldCheck className="h-3.5 w-3.5 text-sky-500" /> : <KeyRound className="h-3.5 w-3.5 text-zinc-500" />}
                                                    {t.title}
                                                </div>
                                                <div className="text-[10px] text-zinc-500 mt-0.5">{t.subtitle}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* TikTok → auth-type tabs (create mode only) */}
                            {credModal.platform === "tiktok" && credModal.mode === "create" && (
                                <div className="mb-4">
                                    <label className="overline">Auth method</label>
                                    <div className="mt-2 grid grid-cols-2 gap-2" data-testid="tiktok-auth-tabs">
                                        {[
                                            { k: "paste_token", title: "Paste Access Token", subtitle: "Recommended · long-lived token from TikTok BC" },
                                            { k: "oauth", title: "OAuth Login", subtitle: "Authorize via TikTok popup" },
                                        ].map((t) => (
                                            <button
                                                type="button"
                                                key={t.k}
                                                onClick={() => setCredModal({ ...credModal, tiktok_auth_type: t.k })}
                                                data-testid={`tiktok-auth-${t.k}`}
                                                className={`text-left px-3 py-2 rounded border transition-colors ${credModal.tiktok_auth_type === t.k ? "border-purple-500 bg-purple-500/5" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                                            >
                                                <div className="text-sm font-medium text-zinc-900 flex items-center gap-1.5">
                                                    {t.k === "paste_token" ? <KeyRound className="h-3.5 w-3.5 text-purple-500" /> : <ShieldCheck className="h-3.5 w-3.5 text-zinc-500" />}
                                                    {t.title}
                                                </div>
                                                <div className="text-[10px] text-zinc-500 mt-0.5">{t.subtitle}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Partner / Advertiser / Business ID field (contextual placeholder) */}
                            <div className="mb-5">
                                <label className="overline flex items-center gap-2">
                                    {credModal.platform === "meta" ? "Business ID (external)" : credModal.platform === "tiktok" ? "TikTok Advertiser ID" : "Partner ID"}
                                    {credModal.platform === "dv360" && (
                                        <span className="text-[10px] text-zinc-500 normal-case tracking-normal">— from DV360 → Partner settings → details</span>
                                    )}
                                    {credModal.platform === "tiktok" && (
                                        <span className="text-[10px] text-zinc-500 normal-case tracking-normal">— from TikTok Ads Manager URL: /ads/manage?aadvid=<code>&lt;this id&gt;</code></span>
                                    )}
                                </label>
                                <input
                                    required
                                    value={credModal.external_id}
                                    onChange={(e) => setCredModal({ ...credModal, external_id: e.target.value })}
                                    placeholder={credModal.platform === "dv360" ? "DV360 numeric Partner ID" : credModal.platform === "tiktok" ? "TikTok numeric Advertiser ID" : "numeric ID"}
                                    className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono"
                                    data-testid="connect-extid-input"
                                />
                            </div>
                            </>
                        )}

                        {/* DV360 → auth-type tabs (edit mode) — lets users switch between SA and User OAuth */}
                        {credModal.platform === "dv360" && credModal.mode === "edit" && (
                            <div className="mb-4">
                                <label className="overline">Auth method</label>
                                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="dv360-auth-tabs-edit">
                                    {[
                                        { k: "service_account", title: "Service Account", subtitle: "Recommended · no consent screen" },
                                        { k: "oauth", title: "User OAuth", subtitle: "Sign in with your Google account" },
                                    ].map((t) => (
                                        <button
                                            type="button"
                                            key={t.k}
                                            onClick={() => setCredModal({ ...credModal, dv360_auth_type: t.k })}
                                            data-testid={`dv360-auth-${t.k}`}
                                            className={`text-left px-3 py-2 rounded border transition-colors ${credModal.dv360_auth_type === t.k ? "border-sky-500 bg-sky-500/5" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                                        >
                                            <div className="text-sm font-medium text-zinc-900 flex items-center gap-1.5">
                                                {t.k === "service_account" ? <ShieldCheck className="h-3.5 w-3.5 text-sky-500" /> : <KeyRound className="h-3.5 w-3.5 text-zinc-500" />}
                                                {t.title}
                                            </div>
                                            <div className="text-[10px] text-zinc-500 mt-0.5">{t.subtitle}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* DV360 Service-Account form */}
                        {credModal.platform === "dv360" && credModal.dv360_auth_type === "service_account" ? (
                            <div className="border-t border-zinc-200 pt-4" data-testid="dv360-sa-section">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <div className="overline">Service-account key</div>
                                        <p className="text-[10px] text-zinc-500 mt-1">Upload the JSON key downloaded from Google Cloud Console (IAM & Admin → Service accounts → Keys → Add key).</p>
                                    </div>
                                    <a href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:wl-accent-text">
                                        <ExternalLink className="h-3 w-3" />open GCP console
                                    </a>
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs text-zinc-700">Upload JSON key file</label>
                                        <input
                                            type="file"
                                            accept="application/json,.json"
                                            onChange={(e) => onSAFileUpload(e.target.files?.[0])}
                                            className="mt-1 block w-full text-xs text-zinc-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-zinc-300 file:bg-white file:text-zinc-700 file:cursor-pointer hover:file:bg-zinc-50"
                                            data-testid="dv360-sa-file"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-zinc-700">Or paste JSON below</label>
                                        <textarea
                                            rows={6}
                                            value={credModal.sa_json_text || ""}
                                            onChange={(e) => onSATextChange(e.target.value)}
                                            placeholder='{"type":"service_account","project_id":"…","client_email":"…@…iam.gserviceaccount.com","private_key":"-----BEGIN PRIVATE KEY-----\n…"}'
                                            className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono text-[11px]"
                                            data-testid="dv360-sa-textarea"
                                        />
                                    </div>
                                    {credModal.sa_error && (
                                        <div className="text-[11px] text-red-500 bg-red-500/5 border border-red-500/20 p-2 rounded">{credModal.sa_error}</div>
                                    )}
                                    {credModal.sa_email && (
                                        <div className="text-[11px] bg-sky-500/5 border border-sky-500/20 p-3 rounded">
                                            <div className="text-zinc-800 font-medium mb-1">Service-account email detected:</div>
                                            <div className="font-mono text-zinc-700 break-all" data-testid="dv360-sa-email-preview">{credModal.sa_email}</div>
                                            <div className="text-zinc-500 mt-2 leading-relaxed">
                                                Before clicking Connect, add this email in <strong>DV360 → Company Settings → Users → + Invite user</strong> with at least Read access to partner <span className="font-mono">{credModal.external_id || "<partner id>"}</span>.
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <label className="overline">Scope / notes (optional)</label>
                                        <textarea rows={2} value={credModal.scope_notes} onChange={(e) => setCredModal({ ...credModal, scope_notes: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" placeholder="Which advertisers did you grant this SA?" />
                                    </div>
                                </div>

                                <p className="mt-4 text-[11px] text-emerald-500 bg-emerald-500/5 border border-emerald-500/20 p-2 rounded">
                                    <strong>SECURE STORAGE:</strong> The JSON key is Fernet-encrypted at rest. Only its <code>client_email</code> and <code>project_id</code> are ever returned to the browser.
                                </p>

                                <div className="flex justify-end gap-2 mt-5">
                                    <button type="button" onClick={() => setCredModal(null)} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-900">Cancel</button>
                                    <button type="submit" disabled={!credModal.sa_json_parsed} className="px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="dv360-sa-submit">
                                        <ShieldCheck className="h-4 w-4" />{credModal.mode === "edit" ? "Update & verify" : "Connect & verify"}
                                    </button>
                                </div>
                            </div>
                        ) : credModal.platform === "tiktok" && credModal.mode === "create" && credModal.tiktok_auth_type === "paste_token" ? (
                            <div className="border-t border-zinc-200 pt-4" data-testid="tiktok-paste-section">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <div className="overline">Long-lived access token</div>
                                        <p className="text-[10px] text-zinc-500 mt-1">Generate one in TikTok Business Developer Portal → your app → <strong>Authorization</strong> → Generate Token for the advertiser above. Valid up to 1 year.</p>
                                    </div>
                                    <a href={PLATFORM_DOCS.tiktok} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:wl-accent-text">
                                        <ExternalLink className="h-3 w-3" />TikTok API docs
                                    </a>
                                </div>

                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs text-zinc-700">TikTok App ID</label>
                                        <input
                                            required
                                            value={credModal.creds.app_id || ""}
                                            onChange={(e) => setCredModal({ ...credModal, creds: { ...credModal.creds, app_id: e.target.value } })}
                                            placeholder="7… numeric App ID from Developer Portal"
                                            className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono"
                                            data-testid="tiktok-app-id"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-zinc-700 flex items-center gap-2">
                                            Long-lived access token
                                            <KeyRound className="h-3 w-3 text-amber-400" />
                                        </label>
                                        <textarea
                                            rows={3}
                                            required
                                            value={credModal.tiktok_access_token || ""}
                                            onChange={(e) => setCredModal({ ...credModal, tiktok_access_token: e.target.value })}
                                            placeholder="e.g. 5abc1234efd… (opaque long string)"
                                            className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono text-[11px]"
                                            data-testid="tiktok-paste-token"
                                        />
                                    </div>
                                    <div>
                                        <label className="overline">Scope / notes (optional)</label>
                                        <textarea rows={2} value={credModal.scope_notes} onChange={(e) => setCredModal({ ...credModal, scope_notes: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" placeholder="Which brand does this advertiser account serve?" />
                                    </div>
                                </div>

                                <div className="mt-4 text-[11px] bg-purple-500/5 border border-purple-500/20 p-3 rounded leading-relaxed">
                                    <div className="text-zinc-800 font-medium mb-1">How agencies get a long-lived token:</div>
                                    <ol className="list-decimal list-inside space-y-0.5 text-zinc-600">
                                        <li>Log into <a href="https://developers.tiktok.com/" target="_blank" rel="noreferrer" className="underline">TikTok for Business Developer Portal</a></li>
                                        <li>Create a <strong>Marketing API</strong> app (or open an existing one)</li>
                                        <li>Enable scopes: <em>Ad Account Management</em> + <em>Reporting (All)</em></li>
                                        <li>Under <strong>Authorization</strong>, click Generate Token for the target Advertiser ID</li>
                                        <li>Copy the token &amp; App ID and paste them above</li>
                                    </ol>
                                </div>

                                <p className="mt-3 text-[11px] text-emerald-500 bg-emerald-500/5 border border-emerald-500/20 p-2 rounded">
                                    <strong>SECURE STORAGE:</strong> The token is Fernet-encrypted at rest and never returned to the browser after save.
                                </p>

                                <div className="flex justify-end gap-2 mt-5">
                                    <button type="button" onClick={() => setCredModal(null)} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-900">Cancel</button>
                                    <button type="submit" disabled={!credModal.tiktok_access_token || !credModal.creds.app_id} className="px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="tiktok-paste-submit">
                                        <ShieldCheck className="h-4 w-4" />Connect &amp; verify
                                    </button>
                                </div>
                            </div>
                        ) : (
                        <div className="border-t border-zinc-200 pt-4">
                            <div className="flex items-center justify-between mb-3">
                                <div>
                                    <div className="overline">Credentials</div>
                                    <p className="text-[10px] text-zinc-500 mt-1">Either paste manually or use OAuth below to authorize in your provider account.</p>
                                </div>
                                <a href={PLATFORM_DOCS[credModal.platform]} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:wl-accent-text">
                                    <ExternalLink className="h-3 w-3" />where do I find these?
                                </a>
                            </div>

                            {/* OAuth shortcut — Meta only (DV360 uses pre-fetched refresh_token) */}
                            {credModal.platform === "meta" && (
                            <div className="mb-4 bg-white border border-zinc-200 p-3 rounded">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <div className="text-xs text-zinc-800 font-medium flex items-center gap-2">
                                            <Link2 className="h-3.5 w-3.5 wl-accent-text" />Connect via OAuth
                                        </div>
                                        <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
                                            Enter only {credModal.platform === "meta" ? "App ID + App Secret + Business ID" : "Client ID + Client Secret + Developer Token + Login Customer ID"} above, then click Connect via OAuth — the {credModal.platform === "meta" ? "Meta" : "Google"} consent window will collect the token for you.
                                        </p>
                                        <p className="text-[10px] text-amber-400/80 mt-1.5 font-mono break-all">
                                            Configure this redirect URI in your {credModal.platform === "meta" ? "Meta app (Facebook Login → Valid OAuth Redirect URIs)" : "Google OAuth client (Authorized redirect URIs)"}:
                                            <br />{redirectUris?.[credModal.platform] || "loading…"}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (credModal.mode === "create") {
                                                toast.error("Save the connection first (Connect & verify), then click 'Connect via OAuth' from the connection card.");
                                                return;
                                            }
                                            try {
                                                const oauthPayload = {
                                                    connection_id: credModal.conn_id,
                                                    platform: credModal.platform,
                                                    ...(credModal.platform === "meta" ? {
                                                        app_id: credModal.creds.app_id || credModal.existing_masked?.app_id || "",
                                                        app_secret: credModal.creds.app_secret || "",
                                                        business_id: credModal.creds.business_id || credModal.existing_masked?.business_id || "",
                                                    } : {
                                                        client_id: credModal.creds.client_id || credModal.existing_masked?.client_id || "",
                                                        client_secret: credModal.creds.client_secret || "",
                                                        developer_token: credModal.creds.developer_token || "",
                                                        login_customer_id: credModal.creds.login_customer_id || credModal.existing_masked?.login_customer_id || "",
                                                    }),
                                                };
                                                const { data } = await api.post("/integrations/oauth/start", oauthPayload);
                                                const popup = window.open(data.authorize_url, "oauth_popup", "width=560,height=720");
                                                if (!popup) toast.error("Popup blocked — allow popups and retry.");
                                            } catch (err) {
                                                toast.error(err.response?.data?.detail || "Failed to start OAuth");
                                            }
                                        }}
                                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium border wl-accent-border hover:bg-zinc-100"
                                        data-testid="oauth-connect-btn"
                                    >
                                        <Link2 className="h-3.5 w-3.5" />Connect via OAuth
                                    </button>
                                </div>
                            </div>
                            )}

                            <div className="space-y-3">
                                {fields.map((f) => (
                                    <div key={f.key}>
                                        <label className="text-xs text-zinc-700 flex items-center gap-2">
                                            {f.label}
                                            {f.secret && <KeyRound className="h-3 w-3 text-amber-400" />}
                                        </label>
                                        {credModal.mode === "edit" && credModal.existing_masked?.[f.key] && !credModal.creds[f.key] && (
                                            <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">Current: {credModal.existing_masked[f.key]}</div>
                                        )}
                                        <div className="relative mt-1">
                                            <input
                                                type={f.secret && !credModal.show?.[f.key] ? "password" : "text"}
                                                value={credModal.creds[f.key] || ""}
                                                onChange={(e) => setCredModal({ ...credModal, creds: { ...credModal.creds, [f.key]: e.target.value } })}
                                                placeholder={credModal.mode === "edit" ? "Leave blank to keep current" : (f.secret ? "" : "")}
                                                required={credModal.mode === "create"}
                                                className="w-full px-3 py-2 bg-zinc-100 border border-zinc-200 rounded font-mono text-sm pr-10"
                                                data-testid={`cred-${f.key}-input`}
                                            />
                                            {f.secret && (
                                                <button
                                                    type="button"
                                                    onClick={() => setCredModal({ ...credModal, show: { ...credModal.show, [f.key]: !credModal.show?.[f.key] } })}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-800"
                                                    tabIndex={-1}
                                                    aria-label="Toggle secret visibility"
                                                >
                                                    {credModal.show?.[f.key] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-zinc-500 mt-0.5">{f.hint}</p>
                                    </div>
                                ))}
                                <div>
                                    <label className="overline">Scope / notes (optional)</label>
                                    <textarea rows={2} value={credModal.scope_notes} onChange={(e) => setCredModal({ ...credModal, scope_notes: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" placeholder="What permissions did you authorize? Which ad accounts are in scope?" />
                                </div>
                            </div>
                        </div>
                        )}

                        {!(
                            (credModal.platform === "dv360" && credModal.dv360_auth_type === "service_account") ||
                            (credModal.platform === "tiktok" && credModal.mode === "create" && credModal.tiktok_auth_type === "paste_token")
                        ) && (
                            <>
                                <p className="mt-4 text-[11px] text-emerald-400/90 bg-emerald-500/5 border border-emerald-500/20 p-2 rounded">
                                    <strong>LIVE INTEGRATION:</strong> Test & Sync call the real Meta Graph API / Google Ads API. Credentials are Fernet-encrypted at rest. If verification fails, the exact error from Meta/Google is shown.
                                </p>

                                <div className="flex justify-end gap-2 mt-5">
                                    <button type="button" onClick={() => setCredModal(null)} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-900">Cancel</button>
                                    <button type="submit" className="px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="connect-submit-btn">
                                        <ShieldCheck className="h-4 w-4" />
                                        {credModal.mode === "create" ? "Connect & verify" : "Save & re-test"}
                                    </button>
                                </div>
                            </>
                        )}
                    </form>
                </div>
            )}

            {history && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setHistory(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-zinc-50 border border-zinc-200 w-full max-w-2xl p-6 rounded-md" data-testid="history-modal">
                        <h3 className="font-display text-xl mb-4">Assignment history</h3>
                        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                            {history.rows.map((r) => (
                                <div key={r.id} className="border border-zinc-200 p-3 bg-zinc-50">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="text-zinc-900 text-sm">{r.advertiser_name}</div>
                                            <div className="text-xs text-zinc-500">{new Date(r.effective_from).toLocaleString()} → {r.effective_to ? new Date(r.effective_to).toLocaleString() : "present"}</div>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${r.is_active ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-zinc-200 text-zinc-500"}`}>
                                            {r.is_active ? "active" : "ended"}
                                        </span>
                                    </div>
                                </div>
                            ))}
                            {!history.rows.length && (<div className="text-zinc-500 text-sm">No history.</div>)}
                        </div>
                        <div className="flex justify-end mt-4">
                            <button onClick={() => setHistory(null)} className="px-3 py-2 text-sm text-zinc-500">Close</button>
                        </div>
                    </div>
                </div>
            )}

            <SyncProgressModal jobId={syncJobId} onClose={() => { setSyncJobId(null); load(); }} />
        </div>
    );
}
